/**
 * Message text-to-speech API slice.
 * Leaf module (Perf 3.1.b/e Step 6). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';
import { getReadAloudProviderVoice } from '@/constants/readAloudVoices';

export const ttsApi = {
  /**
   * Start speech generation for message playback. Charges standard TTS credits
   * (default model: ElevenLabs Eleven v4). The server generates in the
   * background (long scripts can take minutes), so this returns a job handle
   * immediately — poll `speakStatus` with the conversationId until ready.
   * @param {{ text: string, voice?: string, speed?: number }} params
   * @returns {Promise<{ status: string, conversationId: string, estimatedCost: number, totalChunks?: number }>}
   */
  speak: async ({ text, voice = getReadAloudProviderVoice(), speed = 1.0 } = {}) => {
    try {
      const data = await fetchWithAuth('/api/tts/speak', {
        method: 'POST',
        body: { text, voice, speed },
      });

      return {
        status: data.status || 'pending',
        conversationId: data.conversationId,
        estimatedCost: data.estimatedCost || 0,
        totalChunks: data.totalChunks,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        error.errorCode = error.data?.errorCode || error.errorCode;
        error.creditsRequired = error.data?.creditsRequired;
        error.creditsAvailable = error.data?.creditsAvailable;
      }
      throw error;
    }
  },

  /**
   * Poll a speak job started by `speak`.
   * @param {string} conversationId
   * @returns {Promise<{ status: 'pending'|'partial'|'ready'|'failed'|'cancelled', url?: string, chunks?: Array<{index: number, url: string, contentType?: string}>, totalChunks?: number, readyChunks?: number, creditsUsed?: number, cacheHit?: boolean, message?: string, errorCode?: string }>}
   */
  speakStatus: async (conversationId) => {
    try {
      const data = await fetchWithAuth(`/api/tts/speak/${encodeURIComponent(conversationId)}/status`);
      if (data?.status === 'failed' && data?.errorCode) {
        return { ...data, errorCode: data.errorCode };
      }
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        error.errorCode = error.data?.errorCode || error.errorCode;
      }
      throw error;
    }
  },

  cleanup: async (conversationId) => {
    if (!conversationId) return;
    await fetchWithAuth(`/api/tts/speak/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    });
  },
  playback: async (conversationId, event, diagnostic) => fetchWithAuth(
    `/api/tts/speak/${encodeURIComponent(conversationId)}/playback`, {
      method: 'POST', body: { event, ...(diagnostic && { diagnostic }) },
    }),
};
